import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { TCustomer, TSmartVariableDefinition, TFilterGroup, TFilterView } from '@/types/TSmartCRM';
import { DataView } from '@/components/SmartCRM/DataView/DataView';
import { SmartVariableManager } from '@/components/SmartCRM/SmartVariableManager/SmartVariableManager';
import { HeaderSection } from './components/HeaderSection/HeaderSection';
import { useSmartVariables } from 'src/Hooks/SmartCRM/useSmartVariables';
import { ETabs, EViewType } from 'src/enums/ESmartCRM';
import { FieldManagementTable } from '@/components/SmartCRM/SmartVariableManager/FieldManagementTable';
import { useLeads } from 'src/Hooks/SmartCRM/useCustomers';
import { CustomerModal } from '@/components/SmartCRM/Forms/CustomerModal';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getSelectedAgentId } from '@/store/account/selector';
import {
	createGroupByOptions,
	fetchFilteredLeads,
	filterReservedFields,
	showErrorNotification,
	showSuccessNotification,
} from '@/utils/SmartCRM/fieldUtils';
import { createCustomerHandlers, createFieldHandlers } from './components/handlers/CrmHandlers';
import { FilterBuilder } from '@/components/SmartCRM/FilterBuilder/FilterBuilder';
import { createInitialPaginationState } from '@/utils/SmartCRM/paginationUtils';
import {
	handleFilteredLeads,
	createPaginationHandlers,
	handleViewChange,
	handleGroupByChange,
	getCurrentPagination,
	loadFilterViews,
	handleSaveAsView,
	removeFilterView,
	searchCustomers,
} from '@/utils/SmartCRM/crmDashboardUtils';
import OutboundMain from '@/components/SmartCRM/Outbound/OutboundMain';
import Header from '@/components/Header';
import { SoundOutlined } from '@ant-design/icons';
import posthog from 'posthog-js';
import { EPostHogProperties } from 'src/enums/EPostHogProperties';
import { EPostHogEvents } from 'src/enums/EPostHogEvents';

interface SmartCRMDashboardProps {
	isNoviUser: boolean;
	debrand?: boolean;
}

export const SmartCRMDashboard = ({ debrand, isNoviUser }: SmartCRMDashboardProps) => {
	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const isInitialized = useRef(false);

	const [view, setView] = useState<EViewType>(EViewType.TABLE);
	const [searchValue, setSearchValue] = useState('');
	const [activeTab, setActiveTab] = useState<ETabs>(ETabs.CUSTOMERS);
	const [groupBy, setGroupBy] = useState('');
	const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
	const [showFieldModal, setShowFieldModal] = useState(false);
	const [showCustomerModal, setShowCustomerModal] = useState(false);
	const [editingField, setEditingField] = useState<TSmartVariableDefinition | null>(null);
	const [editingCustomer, setEditingCustomer] = useState<TCustomer | null>(null);
	const [deletingFieldId, setDeletingFieldId] = useState<string | null>(null);
	const [filteredCustomers, setFilteredCustomers] = useState<TCustomer[]>([]);
	const [filteredCount, setFilteredCount] = useState<number>(0);
	const [filterLoading, setFilterLoading] = useState(false);
	const [filteredPagination, setFilteredPagination] = useState(createInitialPaginationState());
	const [filterViews, setFilterViews] = useState<TFilterView[]>([]);
	const [activeFilterView, setActiveFilterView] = useState<string | null>(null);
	const [filterViewsLoading, setFilterViewsLoading] = useState(false);

	const {
		customers,
		loading: customersLoading,
		pagination,
		createCustomer,
		updateCustomer,
		deleteCustomer,
		fetchLeads,
		createBulkCustomers,
	} = useLeads(selectedAgentId);
	const { fields, loading: fieldsLoading, saveField, deleteField } = useSmartVariables(isNoviUser);

	const groupByOptions = useMemo(() => {
		const userFields = filterReservedFields(fields);
		return createGroupByOptions(userFields);
	}, [fields]);

	const baseCustomers = useMemo((): TCustomer[] => {
		if (activeFilterView) {
			const activeView = filterViews.find((v) => v.id === activeFilterView);
			if (activeView?.responseGroups) {
				return Object.values(activeView.responseGroups).flat();
			}
			return activeView?.filteredCustomers || filteredCustomers;
		}
		return groupBy ? filteredCustomers : customers;
	}, [activeFilterView, filterViews, filteredCustomers, customers, groupBy]);

	const displayCustomers = useMemo((): TCustomer[] => {
		return searchCustomers(baseCustomers, searchValue);
	}, [baseCustomers, searchValue]);

	const displayLoading = useMemo(
		() => customersLoading || filterLoading || filterViewsLoading,
		[customersLoading, filterLoading, filterViewsLoading],
	);

	const activeView = useMemo(
		() => filterViews.find((v) => v.id === activeFilterView),
		[filterViews, activeFilterView],
	);

	const currentActiveFilterView = useMemo(
		() => filterViews.find((v) => v.id === activeFilterView) || null,
		[filterViews, activeFilterView],
	);

	const currentGroupNames = useMemo(() => {
		if (!activeView?.filterGroups) {
			return [];
		}
		return Object.values(activeView.filterGroups).map((group) => group.name);
	}, [activeView]);

	const handleFilteredLeadsWrapper = useCallback(
		(fieldId: string) =>
			handleFilteredLeads(
				fieldId,
				selectedAgentId,
				view,
				pagination.pageSize,
				setFilterLoading,
				setFilteredCustomers,
				setFilteredCount,
				setFilteredPagination,
			),
		[selectedAgentId, view, pagination.pageSize],
	);

	const paginationHandlers = useMemo(
		() =>
			createPaginationHandlers(
				filteredPagination,
				groupBy,
				fields,
				selectedAgentId,
				view,
				setFilterLoading,
				setFilteredCustomers,
				setFilteredCount,
				setFilteredPagination,
			),
		[filteredPagination, groupBy, fields, selectedAgentId, view],
	);

	const currentPagination = useMemo(
		() =>
			getCurrentPagination(
				activeFilterView,
				groupBy,
				filteredPagination,
				pagination,
				paginationHandlers,
			),
		[activeFilterView, groupBy, filteredPagination, pagination, paginationHandlers],
	);

	const customerHandlers = useMemo(
		() =>
			createCustomerHandlers(
				editingCustomer,
				createCustomer,
				updateCustomer,
				deleteCustomer,
				setShowCustomerModal,
				setEditingCustomer,
				createBulkCustomers,
			),
		[editingCustomer, createCustomer, updateCustomer, deleteCustomer, createBulkCustomers],
	);

	const fieldHandlers = useMemo(
		() =>
			createFieldHandlers(
				editingField,
				saveField,
				deleteField,
				setShowFieldModal,
				setEditingField,
				setDeletingFieldId,
			),
		[editingField, saveField, deleteField],
	);

	useEffect(() => {
		if (selectedAgentId) {
			loadFilterViewsForAgent();
		}
	}, [selectedAgentId]);

	useEffect(() => {
		if (selectedAgentId) {
			posthog.capture(EPostHogEvents.CRM_PAGE_VIEWED, {
				[EPostHogProperties.AGENT_ID]: selectedAgentId,
			});

			const startTime = Date.now();

			return () => {
				const endTime = Date.now();
				const durationInSeconds = Math.round((endTime - startTime) / 1000);

				if (durationInSeconds > 1) {
					posthog.capture(EPostHogEvents.CRM_PAGE_END, {
						[EPostHogProperties.AGENT_ID]: selectedAgentId,
						[EPostHogProperties.DURATION_SECONDS]: durationInSeconds,
					});
				}
			};
		}
	}, [selectedAgentId]);

	useEffect(() => {
		if (fields.length === 0 || isInitialized.current) {
			return;
		}

		if (view === EViewType.BOARD && !groupBy && !activeFilterView) {
			const userFields = filterReservedFields(fields);

			if (userFields.length > 0) {
				setGroupBy(userFields[0].internalKey);
			} else {
				setView(EViewType.TABLE);
			}
		}
		isInitialized.current = true;
	}, [fields, view, groupBy, activeFilterView]);

	const loadFilterViewsForAgent = async (): Promise<void> => {
		if (!selectedAgentId) {
			return;
		}

		try {
			setFilterViewsLoading(true);
			const views = await loadFilterViews(selectedAgentId);
			setFilterViews(deduplicateViews(views));
		} catch (error: any) {
			showErrorNotification('Error loading filter views', error.message);
		} finally {
			setFilterViewsLoading(false);
		}
	};

	const onViewChange = useCallback(
		(newView: EViewType) => {
			handleViewChange(
				newView,
				view,
				groupBy,
				activeFilterView,
				groupByOptions,
				fields,
				setView,
				setGroupBy,
				handleFilteredLeadsWrapper,
			);
		},
		[view, groupBy, activeFilterView, groupByOptions, fields, handleFilteredLeadsWrapper],
	);

	const onGroupByChange = useCallback(
		(value: string) => {
			if (value === '' || value === 'All Groups') {
				handleGroupByChange(
					value,
					fields,
					pagination.pageSize,
					setGroupBy,
					setFilteredCustomers,
					setFilteredCount,
					setFilteredPagination,
					fetchLeads,
					handleFilteredLeadsWrapper,
				);
				return;
			}

			const userFields = filterReservedFields(fields);
			const isValidField = userFields.some((field) => field.internalKey === value);

			if (!isValidField) {
				showErrorNotification('Invalid field selection', 'Cannot group by reserved system fields');
				return;
			}

			handleGroupByChange(
				value,
				fields,
				pagination.pageSize,
				setGroupBy,
				setFilteredCustomers,
				setFilteredCount,
				setFilteredPagination,
				fetchLeads,
				handleFilteredLeadsWrapper,
			);
		},
		[fields, pagination.pageSize, fetchLeads, handleFilteredLeadsWrapper],
	);

	const deduplicateViews = (views: TFilterView[]) => {
		const seen = new Set();
		return views.filter((view) => {
			if (seen.has(view.id)) {
				return false;
			}
			seen.add(view.id);
			return true;
		});
	};

	const onSaveAsView = useCallback(
		async (name: string, filters: any, viewId?: string): Promise<void> => {
			if (!selectedAgentId) {
				return;
			}

			try {
				setFilterLoading(true);
				const savedView = await handleSaveAsView(name, filters, selectedAgentId, view, viewId);

				const result = await fetchFilteredLeads(
					'',
					selectedAgentId,
					view,
					{ limit: 20 },
					savedView.filterGroups,
				);

				if (result) {
					const mappedGroups = {};
					if (result.groups && result.groupMetadata) {
						Object.entries(result.groups).forEach(([groupId, customers]) => {
							const groupInfo = result.groupMetadata[groupId];
							if (groupInfo) {
								mappedGroups[groupInfo.name] = customers;
							}
						});
					}

					const updatedView = {
						...savedView,
						filteredCustomers: result.leads,
						responseGroups: mappedGroups,
					};

					if (viewId) {
						setFilterViews((prev) =>
							deduplicateViews(prev.map((view) => (view.id === viewId ? updatedView : view))),
						);
						showSuccessNotification('Filter view updated successfully');
					} else {
						setFilterViews((prev) =>
							deduplicateViews([...prev, { ...updatedView, isActive: true }]),
						);
						setActiveFilterView(savedView.id);
						showSuccessNotification('Filter view saved successfully');
					}

					setFilteredCustomers(result.leads);
					setFilteredCount(result.count);
				} else {
					if (viewId) {
						setFilterViews((prev) =>
							deduplicateViews(prev.map((view) => (view.id === viewId ? savedView : view))),
						);
					} else {
						setFilterViews((prev) => deduplicateViews([...prev, { ...savedView, isActive: true }]));
						setActiveFilterView(savedView.id);
					}
				}

				setShowAdvancedFilters(false);
			} catch (error: any) {
				showErrorNotification('Error saving filter view', error.message);
			} finally {
				setFilterLoading(false);
			}
		},
		[selectedAgentId, view],
	);

	const onFilterViewChange = useCallback(
		async (viewId: string | null): Promise<void> => {
			if (!selectedAgentId) {
				return;
			}

			setFilterViews((prev) => prev.map((view) => ({ ...view, isActive: view.id === viewId })));
			setActiveFilterView(viewId);
			setActiveTab(ETabs.CUSTOMERS);

			if (viewId) {
				const selectedView = filterViews.find((view) => view.id === viewId);
				if (selectedView) {
					try {
						setFilterLoading(true);
						const result = await fetchFilteredLeads(
							'',
							selectedAgentId,
							view,
							{ limit: 20 },
							selectedView.filterGroups,
						);

						if (result) {
							setFilteredCustomers(result.leads);
							setFilteredCount(result.count);

							const mappedGroups = {};
							if (result.groups && result.groupMetadata) {
								Object.entries(result.groups).forEach(([groupId, customers]) => {
									const groupInfo = result.groupMetadata[groupId];
									if (groupInfo) {
										mappedGroups[groupInfo.name] = customers;
									}
								});
							}

							setFilterViews((prev) =>
								prev.map((view) =>
									view.id === viewId
										? {
												...view,
												filteredCustomers: result.leads,
												responseGroups: mappedGroups,
											}
										: view,
								),
							);

							if (result.groups) {
								setGroupBy(Object.keys(result.groups)[0] || '');
							}
						}
					} catch (error: any) {
						showErrorNotification('Error applying filter view', error.message);
						setActiveFilterView(null);
						setFilterViews((prev) => prev.map((view) => ({ ...view, isActive: false })));
					} finally {
						setFilterLoading(false);
					}
				}
			} else {
				setFilteredCustomers([]);
				setFilteredCount(0);
				setGroupBy('');
				setView(EViewType.TABLE);
			}
		},
		[filterViews, selectedAgentId, view],
	);

	const onCloseFilterView = useCallback(
		async (viewId: string): Promise<void> => {
			if (!selectedAgentId) {
				return;
			}

			try {
				setFilterViewsLoading(true);
				await removeFilterView(selectedAgentId, viewId);
				setFilterViews((prev) => prev.filter((view) => view.id !== viewId));
				if (activeFilterView === viewId) {
					setActiveFilterView(null);
					setView(EViewType.TABLE);
					setGroupBy('');
					setShowAdvancedFilters(false);
				}
				showSuccessNotification('Filter view deleted successfully');
			} catch (error: any) {
				showErrorNotification('Error deleting filter view', error.message);
			} finally {
				setFilterViewsLoading(false);
			}
		},
		[selectedAgentId, activeFilterView],
	);

	const onCustomersTab = useCallback(() => {
		setActiveTab(ETabs.CUSTOMERS);
		setActiveFilterView(null);
		setShowAdvancedFilters(false);

		if (view === EViewType.BOARD && groupByOptions.length > 0) {
			const firstFieldKey = groupByOptions[0].value;
			const firstField = fields.find((field) => field.internalKey === firstFieldKey);

			const userFields = filterReservedFields(fields);
			const isValidField = userFields.some((field) => field.internalKey === firstFieldKey);

			if (firstField?.id && isValidField) {
				setGroupBy(firstFieldKey);
				handleFilteredLeadsWrapper(firstField.id);
			} else if (view === EViewType.BOARD) {
				setView(EViewType.TABLE);
			}
		}
	}, [view, groupByOptions, fields, handleFilteredLeadsWrapper]);

	const onOutboundTaskTab = useCallback(() => {
		setActiveTab(ETabs.OUTBOUND_TASK);
		setActiveFilterView(null);
		setShowAdvancedFilters(false);
	}, []);

	const onSwitchToCustomers = useCallback(() => {
		setActiveFilterView(null);
		setActiveTab(ETabs.CUSTOMERS);
		setShowAdvancedFilters(false);
	}, []);

	const handleFilterChange = useCallback((_filters: any[]) => {}, []);
	const handleApplyFilters = useCallback(async (_filters: TFilterGroup[]) => {}, []);
	const handleSearchChange = useCallback((value: string) => setSearchValue(value), []);
	const handleAdvancedFilters = useCallback(() => setShowAdvancedFilters((prev) => !prev), []);
	const handleManageFields = useCallback(() => {
		setActiveTab(ETabs.FIELDS);
		setActiveFilterView(null);
		setShowAdvancedFilters(false);
	}, []);

	return (
		<div className="h-screen bg-gray-50 flex flex-col">
			<Header
				title="My Customers"
				description="Manage your leads and customer relationships"
				icon={<SoundOutlined />}
				debrand={debrand}
			/>
			<div className="flex-1 flex flex-col overflow-hidden">
				<div className="bg-white flex-shrink-0">
					<HeaderSection
						view={view}
						onViewChange={onViewChange}
						onNewCustomer={customerHandlers.handleNew}
						onManageFields={handleManageFields}
						onCustomersTab={onCustomersTab}
						onAdvancedFilters={handleAdvancedFilters}
						customers={displayCustomers}
						searchValue={searchValue}
						onSearchChange={handleSearchChange}
						groupBy={groupBy}
						onGroupByChange={onGroupByChange}
						groupByOptions={groupByOptions}
						activeTab={activeTab}
						filteredCount={filteredCount}
						filterViews={filterViews}
						activeFilterView={activeFilterView}
						onFilterViewChange={onFilterViewChange}
						onCloseFilterView={onCloseFilterView}
						showAdvancedFilters={showAdvancedFilters}
						onToggleAdvancedFilters={setShowAdvancedFilters}
						onOutboundTaskTab={onOutboundTaskTab}
					/>
					<FilterBuilder
						fields={fields}
						activeFilterView={currentActiveFilterView}
						visible={showAdvancedFilters}
						onFiltersChange={handleFilterChange}
						onApplyFilters={handleApplyFilters}
						onSaveAsView={onSaveAsView}
						onSwitchToCustomers={onSwitchToCustomers}
						isLoading={filterLoading}
					/>
				</div>

				<div className="bg-white flex-1 overflow-auto">
					{(activeTab === ETabs.CUSTOMERS || activeFilterView) && (
						<DataView
							data={displayCustomers}
							fields={fields}
							view={view}
							loading={displayLoading}
							groupBy={
								view === EViewType.BOARD && !activeFilterView
									? groupBy
									: activeFilterView && fields.length > 0
										? fields[0].internalKey
										: ''
							}
							onCustomerAction={customerHandlers.handleAction}
							pagination={
								view === EViewType.TABLE && !activeFilterView ? currentPagination : undefined
							}
							deleteCustomer={deleteCustomer}
							isFilterView={!!activeFilterView}
							filterViewName={activeView?.name}
							customGroupNames={currentGroupNames}
							responseGroups={activeView?.responseGroups}
						/>
					)}

					{activeTab === ETabs.FIELDS && !activeFilterView && (
						<FieldManagementTable
							fields={fields}
							loading={fieldsLoading}
							onAddField={fieldHandlers.handleAdd}
							onEditField={fieldHandlers.handleEdit}
							onDeleteField={fieldHandlers.handleDelete}
							deletingFieldId={deletingFieldId}
						/>
					)}

					{activeTab === ETabs.OUTBOUND_TASK && !activeFilterView && <OutboundMain />}
				</div>
			</div>

			<CustomerModal
				visible={showCustomerModal}
				onCancel={customerHandlers.handleClose}
				onSave={customerHandlers.handleSave}
				onBulkSave={customerHandlers.handleBulkSave}
				initialData={editingCustomer}
				agentId={selectedAgentId}
				variables={fields}
			/>

			<SmartVariableManager
				visible={showFieldModal}
				initialData={editingField}
				onSave={fieldHandlers.handleSave}
				onCancel={fieldHandlers.handleClose}
			/>
		</div>
	);
};
